import type { IconName } from "@amtana/design-system";
import type { SupportedLanguage } from "@amtana/i18n";
import { CONTACT_ADDRESS, PRODUCT_NAME, withProductName } from "@amtana/i18n/catalog";

import { blogFooterLink } from "../features/blog/model/blog-links.ts";
import { COMPARISON_LINKS } from "../features/comparisons/model/comparisons-links.ts";
import { PRICING_LINKS } from "../features/pricing/model/pricing-links.ts";
import { USE_CASE_LINKS } from "../features/use-cases/model/use-cases-links.ts";
import { familyFooterColumns } from "./family-links.ts";
import { LEGAL_CONTENT, LEGAL_PATHS } from "./legal-content.ts";
import type { LegalWords } from "./legal-words.ts";
import { EN } from "./site-words-en.ts";
import { ES } from "./site-words-es.ts";
import type { Words } from "./site-words.ts";

// Every word on the marketing site, in English and Spanish (S10.02). The shape (anchors, links,
// icons, platforms) is built once by `siteContent`; each language only supplies its words.
// `useSiteContent` (behavior/use-site-content.ts) picks the active language. Copy changes here;
// layout changes in ui/.

type SiteLink = { readonly label: string; readonly href: string };

export type JourneyStep = {
  readonly icon: IconName;
  readonly eyebrow: string;
  readonly title: string;
  readonly description: string;
};

export type Feature = {
  readonly icon: IconName;
  readonly title: string;
  readonly description: string;
};

const JOURNEY_ICONS: readonly IconName[] = ["radar", "check-circle", "download"];
const FEATURE_ICONS: readonly IconName[] = ["globe", "check-circle", "sparkles", "download", "bot"];

const linkTo = (section: { readonly id: string; readonly label: string }): SiteLink => ({
  label: section.label,
  href: `#${section.id}`,
});

/** Every string in `words`, at any depth, with `{product}` read as the product's name. */
function named<T>(words: T): T {
  if (typeof words === "string") return withProductName(words) as T;
  if (Array.isArray(words)) return words.map((word: unknown) => named(word)) as T;
  if (words !== null && typeof words === "object") {
    return Object.fromEntries(Object.entries(words).map(([key, value]) => [key, named(value)])) as T;
  }
  return words;
}

/** Fills a phrase's `{name}` places. */
const fill = (text: string, values: Readonly<Record<string, string>>): string =>
  text.replace(/\{(\w+)\}/g, (place, name: string) => values[name] ?? place);

function siteContent(written: Words, locale: SupportedLanguage, legal: LegalWords) {
  const words = named(written);
  const SECTIONS = {
    demo: { id: "demo", label: words.demoLabel },
    howItWorks: {
      id: "how-it-works",
      label: words.howItWorksLabel,
      title: words.howItWorksTitle,
      description: words.howItWorksDescription,
    },
    product: {
      id: "product",
      label: words.productLabel,
      title: words.productTitle,
      description: words.productDescription,
    },
    faq: { id: "faq", label: words.faqLabel, title: words.faqTitle },
    join: { id: "join" },
  };
  return {
    BRAND: { name: PRODUCT_NAME, tagline: words.tagline },
    /** The app's own doors; the site's host sends them on to the platform. */
    ACCOUNT: {
      signIn: { label: words.signIn, href: "/login" },
      signUp: { label: words.signUp, href: "/register" },
    },
    SECTIONS,
    NAV_LINKS: [
      linkTo(SECTIONS.demo),
      linkTo(SECTIONS.howItWorks),
      linkTo(SECTIONS.product),
      linkTo(SECTIONS.faq),
    ] as readonly SiteLink[],
    /** The sections the left rail lists while the reader is past the hero. */
    SECTION_RAIL: [
      linkTo(SECTIONS.howItWorks),
      linkTo(SECTIONS.product),
      linkTo(SECTIONS.faq),
    ] as readonly SiteLink[],
    /** The browser tab's title, per screen. */
    PAGE_TITLES: {
      home: words.homeTitle,
      brand: words.brandTitle,
      notFound: words.notFoundTitle,
      terms: `${legal.terms.title} · ${PRODUCT_NAME}`,
      privacy: `${legal.privacy.title} · ${PRODUCT_NAME}`,
      notice: `${legal.notice.title} · ${PRODUCT_NAME}`,
    },
    /** Every public screen's own search head (ADR-027 point 2, S31.01 and S31.02). */
    SEO: {
      home: { description: words.homeDescription, keyphrase: words.homeKeyphrase },
      brand: { description: words.brandDescription },
      terms: { description: legal.terms.seoDescription },
      privacy: { description: legal.privacy.seoDescription },
      notice: { description: legal.notice.seoDescription },
    },
    HERO: {
      eyebrow: { label: words.heroEyebrow, href: `#${SECTIONS.howItWorks.id}` },
      titleLead: words.heroTitleLead,
      titleTail: words.heroTitleTail,
      /** What the title turns through: an account, its followers and who it follows (D1151). */
      sources: words.heroSources.map((text) => ({ text })),
      sourcesLabel: words.heroSourcesLabel,
      platforms: [
        { label: words.platforms.instagram, source: "instagram" },
        { label: words.platforms.x, source: "twitter" },
        { label: words.platforms.googleMaps, source: "google-maps" },
        { label: words.platforms.tiktok, source: "tiktok" },
        { label: words.platforms.linkedin, source: "linkedin" },
      ] as const,
      ledeLead: words.heroLedeLead,
      ledeTail: words.heroLedeTail,
      /** The one way in from the hero: straight to sign-up on the platform. */
      start: { label: words.heroStart, href: "/register" },
      note: words.heroNote,
    },
    DEMO: {
      handle: "@acme_company",
      placeholder: words.demoPlaceholder,
      action: words.demoAction,
      reading: words.demoReading,
      handleLabel: words.demoHandleLabel,
      stats: {
        followers: words.demoFollowersRead,
        businesses: words.demoBusinesses,
        withEmail: words.demoWithEmail,
      },
      tableLabel: words.demoTableLabel,
      /** "Showing 1–12 of 10,395" */
      showing: (shown: number, total: number): string =>
        fill(words.demoShowing, { shown: String(shown), total: total.toLocaleString(locale) }),
      caption: words.demoCaption,
      columns: words.columns,
      detail: { ...words.detail, platform: words.platforms.instagram },
      places: words.demoPlaces,
      project: words.defaultProject,
      trail: [words.analyses, words.newAnalysis],
      nav: [
        { icon: "dashboard", label: words.nav.dashboard },
        { icon: "chart", label: words.nav.analyses, active: true },
        { icon: "users", label: words.nav.prospects },
      ] as ReadonlyArray<{ readonly icon: IconName; readonly label: string; readonly active?: boolean }>,
    },
    JOURNEY: words.journey.map((step, index) => ({
      icon: JOURNEY_ICONS[index] ?? "radar",
      eyebrow: step.eyebrow ?? "",
      title: step.title,
      description: step.description,
    })) as readonly JourneyStep[],
    FEATURES: words.features.map((feature, index) => ({
      icon: FEATURE_ICONS[index] ?? "sparkles",
      title: feature.title,
      description: feature.description,
    })) as readonly Feature[],
    STATS: words.stats,
    QUESTIONS: words.questions,
    CTA: { title: words.ctaTitle, description: words.ctaDescription },
    FOOTER_COLUMNS: [
      {
        title: words.footer.product,
        links: [
          linkTo(SECTIONS.demo),
          linkTo(SECTIONS.howItWorks),
          { label: words.footer.features, href: `#${SECTIONS.product.id}` },
        ],
      },
      ...named(familyFooterColumns([USE_CASE_LINKS, COMPARISON_LINKS, PRICING_LINKS], locale)),
      {
        title: words.footer.company,
        links: [
          { label: words.footer.brand, href: "/brand" },
          { label: words.footer.contact, href: `mailto:${CONTACT_ADDRESS}` },
        ],
      },
      { title: words.footer.resources, links: [linkTo(SECTIONS.faq), blogFooterLink(locale)] },
      {
        title: legal.chrome.footerTitle,
        links: [
          { label: legal.chrome.terms, href: LEGAL_PATHS.terms },
          { label: legal.chrome.privacy, href: LEGAL_PATHS.privacy },
          { label: legal.chrome.notice, href: LEGAL_PATHS.notice },
        ],
      },
    ] as ReadonlyArray<{ readonly title: string; readonly links: readonly SiteLink[] }>,
    LEGAL: words.legal,
    /** The 404, told in the product's window: the address looked up, nothing found. */
    NOT_FOUND: {
      code: "404",
      trail: [words.analyses, words.notFound.trail],
      addressLabel: words.notFound.addressLabel,
      tableLabel: words.notFound.tableLabel,
      title: words.notFound.title,
      emptyTitle: words.notFound.emptyTitle,
      emptyDescription: words.notFound.emptyDescription,
      description: words.notFound.description,
      home: words.notFound.home,
    },
    COOKIES: {
      title: words.cookies.title,
      body: words.cookies.body,
      reject: words.cookies.reject,
      accept: words.cookies.accept,
      choose: words.cookies.choose,
      choices: {
        title: words.cookies.choicesTitle,
        description: words.cookies.choicesDescription,
        save: words.cookies.save,
        required: {
          title: words.cookies.requiredTitle,
          description: words.cookies.requiredDescription,
          label: words.cookies.requiredLabel,
        },
        categories: [
          {
            id: "analytics" as const,
            title: words.cookies.analyticsTitle,
            description: words.cookies.analyticsDescription,
          },
          {
            id: "advertising" as const,
            title: words.cookies.advertisingTitle,
            description: words.cookies.advertisingDescription,
          },
        ],
      },
    },
  };
}

export type SiteContent = ReturnType<typeof siteContent>;

export const SITE_CONTENT: Readonly<Record<SupportedLanguage, SiteContent>> = {
  en: siteContent(EN, "en", LEGAL_CONTENT.en),
  es: siteContent(ES, "es", LEGAL_CONTENT.es),
};
